import { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Input, InputNumber, notification, Popconfirm, Popover, Row, Spin, Table } from 'antd'
import { MinusSquareOutlined, PlusSquareOutlined, SaveFilled } from '@ant-design/icons'
import { renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  IExpenseByChannelResponseDto,
  deleteExpensesByChannel,
  editExpensesByChannel,
  getExpensesByChannel,
  setExpensesByChannel,
} from 'Services/Omnichannel/ExpenseService'
import { useStore } from 'react-redux'
import { prependCurrencySymbol, removeCurrencySymbol } from 'Helpers/CurrencyHelper'
import SelectMonth from './SelectMonth'
import { TypeOfLock, acquireLock } from 'Services/Omnichannel/LockService'
import ConfirmSaveModal from 'Components/Generic/ConfirmSaveModal'
import { useOutletContext } from 'react-router'
import { isRuleLockedByCurrentUser, isRuleLockedByOtherUsers } from 'Helpers/DiscountHelper'
import { customEqual as lockEquality } from 'Helpers/LockHelper'
import { getDistinctValues } from '../../../Helpers/ArrayHelper'
import { ColumnType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { FixedExpenseColumns } from 'Constants/TableConfiguration'

interface IExpenseByChannelDtoWithOriginalAmount extends IExpenseByChannelResponseDto {
  originalAmount: number
  isNewCol?: boolean
}

const KEY = 'key'
const CHANNEL = FixedExpenseColumns.CHANNEL
const TOTAL = FixedExpenseColumns.TOTAL
const NEW = FixedExpenseColumns.NEW
const PREMI = FixedExpenseColumns.PREMI

export default function ExpensesRegisterTable() {
  const dispatch = useAppDispatch()
  const store = useStore()

  const [isLoading, setIsLoading] = useState(true)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  // Calculate id for lock purpose.
  const id = parseInt(`${String(year)}${String(month)}`)

  const [expenses, setExpenses] = useState<Array<IExpenseByChannelDtoWithOriginalAmount>>([])
  const [newColumn, setNewColumn] = useState<string>(null)
  const [addedColumns, setAddedColumns] = useState<string[]>([])
  const [editCol, setEditCol] = useState<string>(null)

  // Outlet context for check click outside him.
  const bodyrefContext = useOutletContext() as any

  const language = useAppSelector(state => state.present.language.value)
  const locks = useAppSelector(state => state.present.locks.locks, lockEquality)
  const user = useAppSelector(state => state.present.configuration.userValue)

  const isSaveAvailable = useMemo(() => {
    return expenses.some(x => x.amount !== x.originalAmount || x.isNewCol)
  }, [expenses])

  const init = useCallback(() => {
    setIsLoading(true)
    setNewColumn(renderTranslation(language, TranslationKeys.expensesAddNewColumn))
    getExpensesByChannel(store, year, month).then((expenses: IExpenseByChannelDtoWithOriginalAmount[]) => {
      //save the original amount
      expenses.forEach(x => (x.originalAmount = x.amount))
      setExpenses(expenses)
      setIsLoading(false)
    })
  }, [language, month, store, year])

  const handleSetYear = (year: number) => {
    if (isSaveAvailable) {
      setIsConfirmModalOpen(true)
    } else {
      setYear(year)
    }
  }

  const handleSetMonth = (month: number) => {
    if (isSaveAvailable) {
      setIsConfirmModalOpen(true)
    } else {
      setMonth(month)
    }
  }

  const onModalCancel = () => {
    setIsConfirmModalOpen(false)
    init()
  }

  const onModalConfirm = () => {
    saveChanges()
    setIsConfirmModalOpen(false)
  }

  const saveChanges = () => {
    setIsLoading(true)

    const expenseToSave = expenses.filter(x => x.amount !== x.originalAmount || x.isNewCol)
    setExpensesByChannel(store, expenseToSave).then(() => init())
    setAddedColumns([])
  }

  const handleChangeAmount = (newValue: string, channel: string, expenseType: string) => {
    if (isRuleLockedByCurrentUser(locks, id, TypeOfLock.Expenses, user)) {
      setValue(newValue, channel, expenseType)
    } else {
      dispatch(acquireLock(false, id, TypeOfLock.Expenses)).then(x => {
        if (x) {
          setValue(newValue, channel, expenseType)
        }
      })
    }
  }

  const setValue = (newValue: string, channel: string, expenseType: string) => {
    let atLeastOneChange = false
    expenses.forEach(x => {
      if (x.channel === channel && x.expenseType === expenseType) {
        // This check enable value deletion when a cell is setted blank.
        if (x.amount !== 0 && newValue === '') newValue = '0'
        const parsed = parseFloat(removeCurrencySymbol(newValue))
        if (!isNaN(parsed)) {
          // Overwrite the old amount.
          x.amount = parsed
          atLeastOneChange = true
        }
      }
    })
    if (atLeastOneChange) {
      setExpenses(expenses => [...expenses])
    }
  }

  const isRuleLocked = useMemo(() => isRuleLockedByOtherUsers(locks, id, TypeOfLock.Expenses, user), [locks, id, user])

  const handleAddNewCol = useCallback(
    (columnExists: boolean) => {
      if (columnExists) {
        notification.error({
          message: 'Error',
          description: <div>{renderTranslation(language, TranslationKeys.expensesColumnAlreadyExists)}</div>,
          placement: 'topRight',
        })
        return
      }

      const channelNames = getDistinctValues(expenses.map(x => x.channel))
      const formattedDate: string = dayjs().startOf('day').format('YYYY-MM-DDTHH:mm:ss')
      const newExpense: IExpenseByChannelDtoWithOriginalAmount[] = channelNames.map(channel => {
        return {
          channel,
          expenseType: newColumn,
          date: formattedDate,
          amount: 0,
          originalAmount: 0,
          summativeBehaviour: false,
          customExpense: {
            id: 0,
            name: newColumn,
          },
          isNewCol: true,
        }
      })

      setExpenses(prev => {
        return [...prev, ...newExpense]
      })

      setAddedColumns(prev => {
        const updated = [...prev]
        updated.push(newColumn)
        return updated
      })

      setNewColumn(renderTranslation(language, TranslationKeys.expensesAddNewColumn))
    },
    [expenses, language, newColumn],
  )

  const handleDeleteCol = useCallback(
    (columnName: string) => {
      if (addedColumns.includes(columnName)) {
        setExpenses(prev => {
          const prevExpenses = [...prev]
          const updatedExpenses = prevExpenses.filter(x => x.expenseType !== columnName)

          return updatedExpenses
        })

        setAddedColumns(prev => {
          const prevCols = [...prev]
          const updatedCols = prevCols.filter(value => value !== columnName)
          return updatedCols
        })

        return
      }

      const id: number = expenses.find(x => x.expenseType === columnName)?.customExpense?.id
      deleteExpensesByChannel(store, id).then(() => init())
    },
    [addedColumns, expenses, init, store],
  )

  const handleChangeColumnName = useCallback(
    (value: string, columnName: string, oldColumns: string[]) => {
      setEditCol(null)

      if (value === columnName || value === '') return

      if (addedColumns.includes(columnName)) {
        if (addedColumns.includes(value) || oldColumns.includes(value)) {
          notification.error({
            message: 'Error',
            description: <div>{renderTranslation(language, TranslationKeys.expensesColumnAlreadyExists)}</div>,
            placement: 'topRight',
          })
          return
        }

        setExpenses(prev => {
          const prevExpenses = [...prev]
          prevExpenses.forEach((x, index) => {
            if (x.expenseType === columnName) {
              prevExpenses[index].expenseType = value
              prevExpenses[index].customExpense.name = value
            }
          })
          return prevExpenses
        })

        setAddedColumns(prev => {
          const updatedCols = [...prev]
          const indexOfColumnToChange = updatedCols.indexOf(columnName)
          updatedCols[indexOfColumnToChange] = value
          return updatedCols
        })

        return
      }

      const id: number = expenses.find(x => x.expenseType === columnName)?.customExpense?.id
      editExpensesByChannel(store, id, value).then(() => init())
    },
    [addedColumns, expenses, init, language, store],
  )

  const mapColumns = useCallback((): ColumnType<any>[] => {
    let columnNames = getDistinctValues(expenses.map(x => x.expenseType))

    columnNames = columnNames.filter(item => item !== PREMI)
    columnNames = [CHANNEL, ...columnNames, PREMI, TOTAL, NEW]

    const summativeColumns = expenses
      .filter(x => x.summativeBehaviour)
      .map(x => x.expenseType)
      .filter((value, index, array) => array.indexOf(value) === index)

    // Calculate the column objects.
    let columnsObjects = columnNames.map(columnName => {
      const columnObject: ColumnType<any> = {
        title: columnName,
        dataIndex: columnName,
        key: columnName,
      }

      if (columnName === TOTAL) {
        const total = expenses.reduce((total, current) => {
          if (current.summativeBehaviour) return total + current.amount
          else return total - current.amount
        }, 0)
        columnObject.title = prependCurrencySymbol(total)
      }

      if (columnName === NEW) {
        columnObject.fixed = 'right'
        columnObject.title = (
          <div className='cursor-pointer d-flex-c justify-space-between'>
            {editCol === columnName ? (
              <div className='new-col-expense'>
                <Input
                  autoFocus
                  className='pr-2'
                  value={newColumn}
                  onChange={e => setNewColumn(e.target.value)}
                  onBlur={() => setEditCol(null)}
                  onPressEnter={() => setEditCol(null)}
                />
              </div>
            ) : (
              <Popover content={'Modifica il nome della colonna da inserire'}>
                <div onClick={() => setEditCol(columnName)}>{newColumn}</div>
              </Popover>
            )}
            <Popover content={'Aggiungi colonna'}>
              <PlusSquareOutlined onClick={() => handleAddNewCol(columnNames.includes(newColumn))} />
            </Popover>
          </div>
        )
      }

      if (!Object.values(FixedExpenseColumns).includes(columnName as FixedExpenseColumns)) {
        columnObject.title = (
          <div className='d-flex-c justify-space-between'>
            {editCol === columnName ? (
              <div className='new-col-expense'>
                <Input
                  autoFocus
                  className='pr-2'
                  defaultValue={columnName}
                  onBlur={e => handleChangeColumnName(e.target.value, columnName, columnNames)}
                  onPressEnter={e => handleChangeColumnName(e.target.value, columnName, columnNames)}
                />
              </div>
            ) : (
              <Popover content={'Modifica il nome della colonna da inserire'}>
                <div onClick={() => setEditCol(columnName)}>{columnName}</div>
              </Popover>
            )}
            {addedColumns.includes(columnName) ? (
              <MinusSquareOutlined className='cursor-pointer' onClick={() => handleDeleteCol(columnName)} />
            ) : (
              <Popconfirm
                title='Delete the task'
                description='Are you sure to delete this task?'
                onConfirm={() => handleDeleteCol(columnName)}
                onCancel={stopPropagation}
                okText='Yes'
                cancelText='No'
              >
                <MinusSquareOutlined className='cursor-pointer' onClick={stopPropagation} />
              </Popconfirm>
            )}
          </div>
        )
      }

      if (summativeColumns.includes(columnName)) {
        // Customize the column class
        columnObject.className = 'summative'
      }

      return columnObject
    })

    return columnsObjects
  }, [addedColumns, editCol, expenses, handleAddNewCol, handleChangeColumnName, handleDeleteCol, newColumn])

  const mapExpenses = () => {
    const result = new Map<string, {}>()
    expenses.forEach(d => {
      if (!result.has(d.channel)) {
        result.set(d.channel, {})
      }
      let obj = result.get(d.channel)
      obj[KEY] = d.channel
      obj[CHANNEL] = d.channel

      obj[d.expenseType] = (
        <InputNumber
          disabled={isRuleLocked}
          value={d.amount > 0 ? d.amount.toString() : ''}
          controls={false}
          formatter={value => prependCurrencySymbol(parseFloat(value))}
          onPressEnter={e => handleChangeAmount(e.target.value, d.channel, d.expenseType)}
          onBlur={e => handleChangeAmount(e.target.value, d.channel, d.expenseType)}
          size={'small'}
        />
      )
      // Calculate total
      if (!Object.hasOwn(obj, TOTAL)) {
        obj[TOTAL] = 0
      }
      if (d.summativeBehaviour) {
        obj[TOTAL] += d.amount
      } else {
        obj[TOTAL] += d.amount * -1
      }
    })

    // Put eur symbol on total rows if any value else clean the cell
    for (const obj of result.values()) {
      if (obj[TOTAL] !== 0) {
        obj[TOTAL] = prependCurrencySymbol(obj[TOTAL])
      } else {
        obj[TOTAL] = ''
      }
    }
    return result
  }

  useEffect(() => {
    init()
  }, [init])

  useEffect(() => {
    let interval = 0
    if (isSaveAvailable) {
      interval = window.setInterval(() => {
        dispatch(acquireLock(true, id, TypeOfLock.Expenses))
      }, 15000)
    }
    return () => {
      if (interval !== 0) {
        clearInterval(interval)
      }
    }
  }, [dispatch, id, isSaveAvailable])

  useEffect(() => {
    const handleClickOutside = event => {
      if (!isSaveAvailable) return
      if (bodyrefContext && bodyrefContext.current && bodyrefContext.current.contains(event?.target)) {
        //inside outlet
      } else {
        //outside outlet

        // Blocco il click.
        event.preventDefault()

        // Apro modale.
        setIsConfirmModalOpen(true)
      }
    }
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [bodyrefContext, isSaveAvailable])

  const mappedExpenses = expenses ? mapExpenses() : []
  const arrayOfMappedExpenses = Array.from(mappedExpenses.values())
  return (
    <Spin spinning={isLoading} size='large' className='max-h-100'>
      <Row justify={'center'} align={'middle'}>
        <ConfirmSaveModal isOpen={isConfirmModalOpen} onModalCancel={onModalCancel} onModalConfirm={onModalConfirm} />

        <Col span={24} className='channel-setting-upper-row'>
          <Row className='channel-title-box'>
            <div className='channel-title title-box'>
              {renderTranslation(language, TranslationKeys.titleRegisterCosts)}
            </div>
            <SaveFilled
              className={'generic-icon save-icon channel-save-icon' + (isSaveAvailable ? ' active' : '')}
              onClick={isSaveAvailable ? saveChanges : null}
            />
            {isRuleLocked ? (
              <div className='rule-box-editing'>{renderTranslation(language, TranslationKeys.ruleBoxEditing)}</div>
            ) : null}
          </Row>
          <Row className='mb-5'>
            <SelectMonth month={month} onChange={value => handleSetMonth(value)} />
            <InputNumber
              disabled={isLoading}
              size='middle'
              min={0}
              max={9999}
              value={year}
              onPressEnter={e => handleSetYear(parseInt(e.target.value))}
              onStep={e => handleSetYear(e)}
            />
          </Row>
          <Row>
            <Table
              id='expensesRegisterTable'
              className='w-100'
              columns={mapColumns()}
              dataSource={arrayOfMappedExpenses}
              pagination={false}
              scroll={{ x: 'max-content' }}
              tableLayout='fixed'
            ></Table>
          </Row>
        </Col>
      </Row>
    </Spin>
  )
}
